import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Box, Chip, styled, Typography } from '@the-platform-group/component-library'
import { DataGridActionCell } from '@the-platform-group/component-library/DataGrid'
import { Delete as DeleteIcon } from '@the-platform-group/component-library/Icons'

const StyledBox = styled(Box)(() => ({ display: 'flex', flexDirection: 'column' }))

const renderCategoriesCell = ({ value }) => {
  if (!value?.length) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {value.map(category => (
        <Chip key={category} label={category} size="small" />
      ))}
    </Box>
  )
}

const renderUpdatedAtCell = ({ value }) => {
  if (!value) {
    return null
  }
  return <Typography variant="body2">{new Date(value).toLocaleString()}</Typography>
}

/**
 * Builds memoized DataGrid column definitions for the agentic prompts list.
 * @param {object} handlers - row action callbacks
 * @param {function} handlers.onDelete - called with the prompt row when the delete action is clicked
 * @returns {Array<object>} DataGrid column definitions
 */
const useColumnConfig = ({ onDelete }) =>
  useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        renderCell: ({ id, value }) => (
          <StyledBox>
            <Link to={`/ai-prompts/${id}`}>{value}</Link>
            <Typography variant="body2" component="span" color="text.secondary">
              {id}
            </Typography>
          </StyledBox>
        ),
      },
      {
        field: 'categories',
        headerName: 'Categories',
        flex: 1.5,
        sortable: false,
        renderCell: renderCategoriesCell,
      },
      {
        field: 'parentPromptTitle',
        headerName: 'Parent',
        flex: 1,
        renderCell: ({ value }) =>
          value ? (
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
          ) : null,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        flex: 1,
        renderCell: renderUpdatedAtCell,
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        flex: 0.5,
        getActions: ({ row }) => [
          <DataGridActionCell
            key="delete"
            icon={<DeleteIcon />}
            title="Delete prompt"
            onClick={() => onDelete(row)}
          />,
        ],
      },
    ],
    [onDelete],
  )

export default useColumnConfig
